import type { PersonRef } from '@mona-roadmap/domain';
import { Check } from 'lucide-react';
import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Avatar } from './Visuals';

export interface OptionItem {
  value: string;
  label: string;
  /** monday hex colour, shown as a dot */
  color?: string;
  count?: number;
  person?: PersonRef;
  hint?: string;
}

interface Props {
  items: OptionItem[];
  selected: readonly string[];
  multiple: boolean;
  onToggle: (value: string) => void;
  /** Search box above the list. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Excel-like bulk actions (multi-select): applied to the currently visible (searched) items. */
  onSelectAll?: (visible: string[]) => void;
  onClearSelection?: () => void;
  /** Single-select "no value" entry. */
  clearOption?: { label: string; onSelect: () => void };
  emptyText?: string;
  label: string;
  maxHeight?: number;
  footer?: ReactNode;
}

/** Searchable single / multi select list with full keyboard support (shared by pickers and filters). */
export function OptionList({
  items,
  selected,
  multiple,
  onToggle,
  searchable = true,
  searchPlaceholder = 'Search...',
  onSelectAll,
  onClearSelection,
  clearOption,
  emptyText = 'No matches',
  label,
  maxHeight = 260,
  footer,
}: Props) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((i) => i.label.toLowerCase().includes(q)) : items;
  }, [items, query]);

  // Navigable rows: optional clear entry first, then items.
  const rows: Array<{ kind: 'clear' } | { kind: 'item'; item: OptionItem }> = [
    ...(clearOption && !query ? [{ kind: 'clear' as const }] : []),
    ...visible.map((item) => ({ kind: 'item' as const, item })),
  ];
  const clamped = Math.min(active, Math.max(0, rows.length - 1));

  const activate = (i: number) => {
    const row = rows[i];
    if (!row) return;
    if (row.kind === 'clear') clearOption?.onSelect();
    else onToggle(row.item.value);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(Math.min(rows.length - 1, clamped + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(Math.max(0, clamped - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(clamped);
    }
  };

  const optionId = (i: number) => `${listId}-${i}`;

  return (
    <div className="flex min-w-[220px] flex-col" onKeyDown={onKeyDown}>
      {searchable ? (
        <div className="border-b border-line p-1.5">
          <input
            autoFocus
            className="input"
            placeholder={searchPlaceholder}
            aria-label={`Search ${label}`}
            role="combobox"
            aria-expanded
            aria-controls={listId}
            aria-activedescendant={rows.length > 0 ? optionId(clamped) : undefined}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
          />
        </div>
      ) : null}
      {multiple && (onSelectAll || onClearSelection) ? (
        <div className="flex items-center gap-1 border-b border-line px-1.5 py-1">
          {onSelectAll ? (
            <button type="button" className="btn btn-ghost !h-8 !px-2 text-[13.5px]" onClick={() => onSelectAll(visible.map((v) => v.value))}>
              Select All
            </button>
          ) : null}
          {onClearSelection ? (
            <button type="button" className="btn btn-ghost !h-8 !px-2 text-[13.5px]" onClick={onClearSelection}>
              Clear Selection
            </button>
          ) : null}
        </div>
      ) : null}
      <div
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={label}
        aria-multiselectable={multiple || undefined}
        tabIndex={searchable ? -1 : 0}
        autoFocus={!searchable}
        className="overflow-y-auto p-1"
        style={{ maxHeight }}
      >
        {rows.length === 0 ? <div className="px-2 py-3 text-center text-faint">{emptyText}</div> : null}
        {rows.map((row, i) => {
          if (row.kind === 'clear') {
            return (
              <div key="__clear" id={optionId(i)} role="option" aria-selected={selected.length === 0} data-active={i === clamped} className="option text-dim" onClick={() => activate(i)} onMouseMove={() => setActive(i)}>
                <span className="w-4" />
                <span className="flex-1 italic">{clearOption?.label}</span>
                {selected.length === 0 ? <Check className="size-3.5 text-accent-ink" aria-hidden /> : null}
              </div>
            );
          }
          const { item } = row;
          const isSel = selected.includes(item.value);
          return (
            <div key={item.value} id={optionId(i)} role="option" aria-selected={isSel} data-active={i === clamped} data-value={item.value} className="option" onClick={() => activate(i)} onMouseMove={() => setActive(i)}>
              {multiple ? (
                <span className={`grid size-4 flex-none place-items-center rounded border ${isSel ? 'border-accent bg-accent text-white' : 'border-line-strong'}`} aria-hidden>
                  {isSel ? <Check className="size-3" strokeWidth={3} /> : null}
                </span>
              ) : null}
              {item.person ? <Avatar person={item.person} /> : null}
              {item.color ? <span className="dot" style={{ '--c': item.color } as React.CSSProperties} aria-hidden /> : null}
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {item.hint ? <span className="text-[12.5px] text-faint">{item.hint}</span> : null}
              {item.count !== undefined ? <span className="text-[12.5px] text-faint">{item.count}</span> : null}
              {!multiple && isSel ? <Check className="size-3.5 text-accent-ink" aria-hidden /> : null}
            </div>
          );
        })}
      </div>
      {footer}
    </div>
  );
}
